import React, { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setHourlyWage,
  setHoursPerWeek,
  setOffDaysPerWeek,
  setAnnualSalary,
  setTotalHours,
  resetSalaryData,
} from "../redux/calculations/salarySlice";
import {
  resetChartData,
  updateSalaryCalculation,
} from "../redux/calculations/calculaterSlice";
import CalculatorLayout from "../components/CalcLayout";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import { Helmet } from "react-helmet";

const SalaryCalc = () => {
  const dispatch = useDispatch();
  const { hourlyWage, hoursPerWeek, offDaysPerWeek } = useSelector(
    (state) => state.salaryCalculator
  );
  const { textData } = useLocale();
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const [calcDone, setCalcDone] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);

  const { annualSalaryCalc } = textData;
  const formik = useFormik({
    initialValues: {
      hourlyWage: hourlyWage,
      hoursPerWeek: hoursPerWeek,
      offDaysPerWeek: offDaysPerWeek,
    },
    validationSchema: Yup.object({
      hourlyWage: Yup.number()
        .min(1, annualSalaryCalc.hourlyWageMin)
        .max(5000, annualSalaryCalc.hourlyWageMax)
        .required(annualSalaryCalc.hourlyWageRequired),
      hoursPerWeek: Yup.number()
        .min(5, annualSalaryCalc.hoursPerWeekMin)
        .max(84, annualSalaryCalc.hoursPerWeekMax)
        .required(annualSalaryCalc.hoursPerWeekRequired),
      offDaysPerWeek: Yup.number()
        .min(1, annualSalaryCalc.offDaysPerWeekMin)
        .max(7, annualSalaryCalc.offDaysPerWeekMax)
        .required(annualSalaryCalc.offDaysPerWeekRequired),
    }),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_SALARY");
      }
      const hourlyRate = parseFloat(values.hourlyWage);
      const hoursWeekly = parseFloat(values.hoursPerWeek);
      const offDays = parseFloat(values.offDaysPerWeek);

      // Calculate working days per year
      const workingDaysPerWeek = 7 - offDays;
      const totalWorkingDays = workingDaysPerWeek * 52; // 52 weeks in a year

      const totalWorkingHours = hoursWeekly * 52;

      const roundedWorkingHours = Math.round(totalWorkingHours);

      const annualSalary = hourlyRate * hoursWeekly * 52;

      dispatch(setHourlyWage(values.hourlyWage));
      dispatch(setHoursPerWeek(values.hoursPerWeek));
      dispatch(setOffDaysPerWeek(values.offDaysPerWeek));
      dispatch(setAnnualSalary(annualSalary));
      dispatch(setTotalHours(roundedWorkingHours));

      dispatch(
        updateSalaryCalculation({
          hourlyWage: hourlyRate,
          annualSalary: annualSalary,
          totalWorkingHours: totalWorkingHours,
          totalWorkingDays: totalWorkingDays,
        })
      );
      setShowTable(false);
      setCalcDone(true);
      setIsFormModified(false);
    },
  });
  useEffect(() => {
    return () => {
      dispatch(resetChartData());
      dispatch(resetSalaryData());
    };
  }, [dispatch]);

  const hourlyWageSliderRef = useRef(null);

  const handleHourlyWageSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setHourlyWage(value));
    formik.setFieldValue("hourlyWage", value);
    setIsFormModified(true);
  };

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";

    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
        shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
      );
  };

  useEffect(() => {
    dispatch(setHourlyWage(formik.values.hourlyWage));
  }, [formik.values, dispatch]);

  useEffect(() => {
    if (isFormModified) {
      setCalcDone(false);
    }
  }, [isFormModified]);

  const handleHourlyWageChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      formik.setFieldValue("hourlyWage", "");
      return;
    }

    const rawValue = inputValue.replace(/[^0-9]/g, "");
    if (rawValue.length > 6) return;

    if (rawValue === "0") {
      formik.setFieldValue("hourlyWage", 0);
      return;
    }

    formik.setFieldValue("hourlyWage", rawValue ? Number(rawValue) : "");
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleHoursPerWeekChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      formik.setFieldValue("hoursPerWeek", "");
      return;
    }

    const rawValue = inputValue.replace(/[^0-9]/g, "");
    if (rawValue.length > 3) return;

    if (rawValue === "0") {
      formik.setFieldValue("hoursPerWeek", 0);
      return;
    }

    formik.setFieldValue("hoursPerWeek", rawValue ? Number(rawValue) : "");
    setIsFormModified(true);
  };
  const handleWeekoffChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("offDaysPerWeek", rawValue);
      return;
    }
    if (rawValue.length > 1) return;
    const isValidInput = /^[0-9]*\.?[0-9]*$/.test(rawValue);
    if (!isValidInput) return;
    formik.setFieldValue("offDaysPerWeek", rawValue);
    setIsFormModified(true);
  };
  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const descriptionContent = (
    <div className="p-4 sm:p-5 w-full bg-[#F6F8FB] rounded-xl">
      <h2 className="text-black text-base sm:text-xl font-medium">
        {" "}
        {textData.salaryContent.title}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.salaryContent.description}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {" "}
        {textData.salaryContent.workdescription}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.salaryContent.howItWorksDescription}
      </p>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.salaryContent.howItWorksAdditionalDescription}
      </p>

      <div className="bg-[#E0E4F0] ">
        <p className="font-medium text-sm sm:text-base p-3 mt-3">
          <strong> {textData.salaryContent.formula}:</strong>
        </p>
        <p className="font-medium text-sm sm:text-base p-3 mt-3">
          {textData.salaryContent.salaryFormula}
        </p>
      </div>

      <table className="border-collapse mt-4 w-full">
        <tbody>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              {textData.salaryContent.htscSymbolsTable[0].symbol}
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.salaryContent.htscSymbolsTable[0].description}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              {textData.salaryContent.htscSymbolsTable[1].symbol}
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.salaryContent.htscSymbolsTable[1].description}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              {textData.salaryContent.htscSymbolsTable[2].symbol}
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.salaryContent.htscSymbolsTable[2].description}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.salaryContent.exampleTitle}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.salaryContent.exampleDescription}
      </p>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li> {textData.salaryContent.exampleDetails[0].label} = 7 − 2 = 5</li>
        <li>
          {textData.salaryContent.exampleDetails[1].label} = 40 × 52 = 2,080{" "}
          {textData.salaryContent.hour}
        </li>
        <li>
          {textData.salaryContent.exampleDetails[2].label} = {textData.currency}
          1,000 × 2,080 = {textData.currency}20,80,000
        </li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.salaryContent.notesTitle}
      </h2>

      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <li
            dangerouslySetInnerHTML={{
              __html: textData.salaryContent.notes[0],
            }}
          />
        </li>
        <li>{textData.salaryContent.notes[1]}</li>
        <li> {textData.salaryContent.notes[2]}</li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.salaryContent.benefitsTitle}
      </h2>

      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>{textData.salaryContent.benefits[0]}</li>
        <li>{textData.salaryContent.benefits[1]}</li>
        <li>{textData.salaryContent.benefits[2]}</li>
        <li>{textData.salaryContent.benefits[3]}</li>
      </ul>
    </div>
  );

  const getPlaceholderValue = (language) => {
    const baseValue = 1000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };
  return (
    <>
      <Helmet>
        <title>Hourly to Salary Calculator – Convert Wages Instantly</title>
        <meta
          name="description"
          content="Easily convert hourly pay to salary online with our free annual salary calculator. Plan budgets, manage income, and calculate your earnings efficiently."
        />
        <link
          rel="canonical"
          href="https://growlifefund.com/calculator/salary-calculator"
        />

        <meta
          property="og:title"
          content="Hourly to Salary Calculator – Convert Wages Instantly"
        />
        <meta
          property="og:description"
          content="Easily convert hourly pay to salary online with our free annual salary calculator. Plan budgets, manage income, and calculate your earnings efficiently."
        />
        <meta
          property="og:url"
          content="https://growlifefund.com/calculator/salary-calculator"
        />
        <meta property="og:site_name" content="growlifefund" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en" />
        <meta
          property="og:image:alt"
          content="Free Hourly to Salary Calculator Online"
        />

        <meta name="robots" content="index, follow" />
      </Helmet>
      <CalculatorLayout
        title={
          <>
            {" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.your}{" "}
            </span>
            {textData.annualSalaryCalc.title}{" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.annualSalaryCalc.salary}{" "}
            </span>
          </>
        }
        description={textData.annualSalaryCalc.description}
        descriptionContent={descriptionContent}
        setShowTable={setShowTable}
        showTable={showTable}
        calcDone={calcDone}
        setCalcDone={setCalcDone}
        isFormModified={isFormModified}
        setIsFormModified={setIsFormModified}
      >
        <div className="">
          <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
            <div className="flex justify-between gap-0 flex-col xs:flex-row  xs:gap-5">
              <div className="w-full">
                <div className=" justify-between mb-2 gap-4 flex flex-col items-start">
                  <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                    {textData.annualSalaryCalc.hoursPerWeekLabel}
                  </label>
                  <div className="flex items-start flex-col w-full">
                    <Input
                      name="hoursPerWeek"
                      value={formatToINR(formik.values.hoursPerWeek)}
                      onChange={handleHoursPerWeekChange}
                      onBlur={formik.handleBlur}
                      type="number"
                      placeholder={"40"}
                      borderColor={
                        formik.touched.hoursPerWeek &&
                          formik.errors.hoursPerWeek
                          ? "border-danger"
                          : "border-gray-light"
                      }
                      className={`w-full sm:w-full border ${formik.touched.hoursPerWeek &&
                          formik.errors.hoursPerWeek
                          ? "border-danger"
                          : "border-gray-light"
                        }`}
                    />
                    {formik.touched.hoursPerWeek &&
                      formik.errors.hoursPerWeek && (
                        <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                          {formik.errors.hoursPerWeek}
                        </div>
                      )}
                  </div>
                </div>
              </div>

              <div className="w-full">
                <div className=" justify-between mb-2 gap-4 flex flex-col items-start">
                  <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                    {textData.annualSalaryCalc.offDaysPerWeekLabel}
                  </label>
                  <div className="flex  flex-col w-full items-start">
                    <Input
                      name="offDaysPerWeek"
                      value={formik.values.offDaysPerWeek}
                      onChange={handleWeekoffChange}
                      onBlur={formik.handleBlur}
                      type="number"
                      placeholder={"2"}
                      borderColor={
                        formik.touched.offDaysPerWeek &&
                          formik.errors.offDaysPerWeek
                          ? "border-danger"
                          : "border-gray-light"
                      }
                      className={`w-full sm:w-full border ${formik.touched.offDaysPerWeek &&
                          formik.errors.offDaysPerWeek
                          ? "border-danger"
                          : "border-gray-light"
                        }`}
                    />
                    {formik.touched.offDaysPerWeek &&
                      formik.errors.offDaysPerWeek && (
                        <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                          {formik.errors.offDaysPerWeek}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.annualSalaryCalc.hourlyWageLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full" ref={hourlyWageSliderRef}>
                  {/* Background track */}

                  <input
                    type="range"
                    min={1}
                    max={5000}
                    // step={1}
                    value={hourlyWage}
                    onChange={handleHourlyWageSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((hourlyWage - 1) / (5000 - 1)) * 100}%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="hourlyWage"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.hourlyWage)}
                    onChange={handleHourlyWageChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getPlaceholderValue(i18n.language)}
                    borderColor={
                      formik.touched.hourlyWage && formik.errors.hourlyWage
                        ? "border-danger"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px] border ${formik.touched.hourlyWage && formik.errors.hourlyWage
                        ? "border-danger"
                        : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.hourlyWage && formik.errors.hourlyWage && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.hourlyWage}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-[10px]">
              <Button type="submit" className="w-full">
                {textData.calculate}
              </Button>
            </div>
          </form>
        </div>
      </CalculatorLayout>
    </>
  );
};

export default SalaryCalc;
